import React, { FC, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader/PageHeader';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { EditCurrentOrganizationDialog } from '../components/EditOrganizationDialog/EditCurrentOrganizationDialog';
import { useDialog } from '../hooks/useDialog';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { Link as RouterLink } from 'react-router-dom';
import { AccountSingleData } from '../components/AccountData/AccountSingleData';
import { Date } from '../components/Date/Date';
import { DateFormat } from '../helpers/dateFormat';
import { OrganizationUsersFetcher } from '../fetchers/OrganizationUsersFetcher';
import { ChangeUserRoleInOrganizationDialog } from '../components/UserRoleInOrganization/ChangeUserRoleInOrganizationDialog';
import { EOrganizationSelectionStatus, IOrganizationContext } from '../contexts/OrganizationContext';
import { countries } from '../helpers/countries';

export const CurrentOrganizationDetailsPage: FC = () => {
  const { t } = useTranslation();
  const { organization } = useContext(OrganizationContext);
  const { data: usersData, onRefresh } = useContext(OrganizationUsersFetcher.Context);

  const { isOpen: isEdit, openUrl: editDialogUrl, close: closeDialog } = useDialog('edit');

  const orgCtxProv = useMemo<IOrganizationContext>(
    () => ({
      selectionStatus: EOrganizationSelectionStatus.Injected,
      organization: organization ?? null,
      organizationId: organization?.id ?? null,
      setOrganization: () => {
        throw new Error('cannot be performed in this context');
      },
    }),
    [organization]
  );

  if (!organization) return null;

  return (
    <div>
      <PageHeader title={organization.name}>
        <Button component={RouterLink} to={editDialogUrl} variant="contained" color="primary" replace={true}>
          {t('common.button.edit')}
        </Button>
      </PageHeader>

      <Grid container spacing={1}>
        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('common.tableHeader.id')}>{organization.id}</AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('common.tableHeader.organization')}>{organization.name}</AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.address')}>{organization.address}</AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.billingAddress')}>{organization.billing_address}</AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('common.tableHeader.phone')}>
            {organization.phone_number || '-'}
          </AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.country')}>
            {organization.country
              ? `${organization.country} ${countries.find((country) => country.code === organization.country)?.name}`
              : '-'}
          </AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('organization.lastUpdated')}>
            <Date date={organization.updated_at} format={DateFormat.DateWithFullTime} />
          </AccountSingleData>
        </Grid>
      </Grid>

      <OrganizationContext.Provider value={orgCtxProv}>
        {usersData ? <ChangeUserRoleInOrganizationDialog users={usersData.payload.data} onRefresh={onRefresh} /> : null}
      </OrganizationContext.Provider>

      <EditCurrentOrganizationDialog open={isEdit} closeDialog={closeDialog} />
    </div>
  );
};
